import {RouteConfig} from 'vue-router'
import redirectPaths from '../redirect-paths'

/**
 * Production routes
 */
const routes: RouteConfig[] = [
  {
    path: 'bizs/:bizId',
    name: 'home',
    alias: 'bizs/:bizId/home',
    component: () => (import(/* webpackPrefetch: true */'@/pages/Home.vue')),
    meta: {
      middleware: ['authenticated', 'user-action', 'from-home-screen', 'set-biz'],
    },
    props() {
      return {
        translation: 'pages.home',
      }
    },
  },
  /**************************************
  * tax
  */
  {
    path: 'bizs/:bizId/taxes',
    name: 'taxes',
    component: () => (import(/* webpackPrefetch: true */'@/pages/Taxes.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
    },
    props(ctx) {
      return {
        translation: 'pages.taxes',
        ...ctx.query,
      }
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/taxes/:taxId',
    name: 'tax-detail',
    component: () => (import(/* webpackPrefetch: true */'pages/Tax.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 2,
    },
    props(ctx) {
      const {params} = ctx
      return {
        translation: 'pages.tax',
        ...params,
      }
    },
  },

  /**************************************
  * invoice
  */
  {
    path: 'bizs/:bizId/invoices/received',
    name: 'received-invoices',
    component: () => (import('@/pages/received-invoices/Index.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
    },
    props(ctx) {
      return {
        translation: 'pages.invoices',
        ...ctx.query,
      }
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/invoices/issued',
    name: 'issued-invoices',
    component: () => (import('@/pages/issued-invoices/Index.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
    },
    props(ctx) {
      return {
        ...ctx.query,
      }
    },
  },

  /**************************************/
  {
    path: 'bizs/:bizId/invoices/received/:invoiceId',
    name: 'received-invoice-detail',
    component: () => (import('@/pages/ReceivedInvoice.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 2,
    },
    props(ctx) {
      const {params} = ctx
      return {
        translation: 'pages.invoices',
        ...params,
      }
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/invoices/issued/:invoiceId',
    name: 'issued-invoice-detail',
    component: () => (import('@/pages/IssuedInvoice.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 2,
    },
    props(ctx) {
      const {params} = ctx
      return {
        ...params,
      }
    }
  },

  /**************************************/
  {
    path: 'external/invoices/:invoiceId',
    name: 'external-invoice-detail',
    component: () => (import('@/pages/ExternalInvoice.vue')),
    props(ctx) {
      const {params} = ctx
      return {
        ...params,
      }
    }
  },

  /**************************************
   * invoice document
   */
  {
    path: 'bizs/:bizId/issue-document',
    name: 'issue-document',
    component: () => (import('@/pages/InvoiceDocument.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
    },
  },

  /**************************************/
  {
    path: 'bizs/:bizId/issued-document/:invoiceId',
    name: 'issued-document',
    component: () => (import('@/pages/IssuedInvoiceDocument.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
    },
    props(ctx) {
      const {params} = ctx
      return {
        ...params,
      }
    },
  },

  /**************************************/
  {
    path: '/external-document/:invoiceId',
    name: 'external-issued-document',
    component: () => (import('@/pages/ExternalInvoiceDocument.vue')),
    props(ctx) {
      const {params} = ctx
      return {
        ...params,
      }
    },
  },

  /**************************************
  * transfer
  */
  {
    path: 'bizs/:bizId/transfer-confirm',
    name: 'transfer-confirm',
    component: () => (import('@/pages/TransferConfirm.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 3,
    },
  },
  {
    path: 'bizs/:bizId/transfer-results',
    name: 'transfer-results',
    component: () => (import('@/pages/TransferResults.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 4,
    },
    props() {
      return {translation: 'terms'}
    },
  },

  /**************************************
   * issue
   */
  {
    path: 'bizs/:bizId/issue',
    name: 'issue',
    component: () => (import('@/pages/issue/Index.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz', 'set-random-id-query'],
    },
  },

  /*************************************
   * Sign-in
   */
  {
    path: 'sign-in',
    name: 'sign-in',
    component: () => (import('pages/SignIn.vue')),
    props(router) {
      const {query} = router
      const state = query.state && JSON.parse(decodeURI(query.state as string))
      return {
        then: query.then || state && state.then,
        code: query.code,
      }
    },
    meta: {
      middleware: 'unauthenticated',
    },
  },
  /*************************************
   * oauth redirect sign-in or invitation
   */
  {
    path: 'oauth',
    name: 'oauth',
    redirect: (router) => {
      const {query} = router
      const {
        from = {name: 'sign-in'},
      } = query.state && JSON.parse(decodeURI(query.state as string))
      return {
        ...from
      }
    },
  },
  /*****************************************/
  {
    path: 'sign-up/phone',
    name: 'sign-up-phone',
    component: () => (import('@/pages/sign-up/User.vue')),
    meta: {
      middleware: [
        {
          name: 'authenticated',
          options: {
            allowNoPhone: true,
            allowNoBiz: true,
          },
        },
      ],
    },
    props() {
      return {
        translation: 'pages.sign-up.user',
      }
    },
  },
  /*****************************************/
  {
    path: 'sign-up/biz',
    name: 'sign-up-biz',
    component: () => (import('@/pages/sign-up/Biz.vue')),
    meta: {
      middleware: [
        {
          name: 'authenticated',
          options: {
            allowNoPhone: false,
            allowNoBiz: true,
          },
        },
      ],
    },
    props() {
      return {
        translation: 'pages.sign-up.biz',
      }
    },
  },

  /**************************************
  * setting
  */
  {
    path: 'bizs/:bizId/setting',
    name: 'setting',
    component: () => (import('pages/Setting.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/cert-failed-reason/:name',
    name: 'cert-failed-reason',
    component: () => (import('pages/CertFailedReason.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 3,
    },
    props(ctx) {
      return {
        translation: 'terms.cert-center',
        ...ctx.params,
      }
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/cert-success-lists/:name',
    name: 'cert-success-lists',
    component: () => (import('pages/CertSuccessLists.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 3,
    },
    props(ctx) {
      return {
        translation: 'terms.cert-center',
        ...ctx.params,
      }
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/cert-center',
    name: 'cert-center',
    component: () => (import('pages/CertCenter.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 2,
    },
    props() {
      return {translation: 'terms.cert-center'}
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/user-center',
    name: 'user-center',
    component: () => (import('pages/UserCenter.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 2,
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/noti',
    name: 'noti',
    component: () => (import('pages/Noti.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 2,
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/notice',
    name: 'notice',
    component: () => (import('pages/Notice.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 2,
    },
    props(ctx) {
      return {
        translation: 'pages.notice',
        ...ctx.query,
      }
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/certificates',
    name: 'certificates',
    alias: ['taxes/certificates', 'taxes/certificates'],
    component: () => (import(/* webpackPrefetch: true */'pages/Certificates.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 3,
    },
    props() {
      return {
        translation: 'pages.certificates',
      }
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/user',
    name: 'user',
    component: () => (import(
      /* webpackPrefetch: true */
      'pages/User.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 2,
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/user/my',
    name: 'user-my-info',
    component: () => (import('pages/user/MyInfo.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 3,
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/user/company',
    name: 'user-company-info',
    component: () => (import('pages/user/CompanyInfo.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 3,
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/create-biz',
    name: 'create-biz',
    component: () => (import('@/pages/sign-up/Biz.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 2,
    },
    props() {
      return {
        translation: 'pages.sign-up.biz',
      }
    },
  },
  /**************************************/
  {
    path: 'bizs/:bizId/banks',
    name: 'banks',
    component: () => (import(/* webpackPrefetch: true */'pages/Banks.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
      pageLevel: 2,
    },
    props() {
      return {
        translation: 'terms.banks',
      }
    },
  },

  /**************************************
  * invitations
  */
  {
    path: 'invitations/:invitationId',
    name: 'invitation',
    component: () => (import('pages/invitation/Start.vue')),
    props(router) {
      const {query} = router
      const state = query.state && JSON.parse(decodeURI(query.state as string))
      return {
        then: query.then || state && state.then,
        code: query.code,
      }
    },
    meta: {
      middleware: ['invitation'],
    }
  },
  {
    path: 'invitations/:invitationId/name',
    name: 'invitation-name',
    component: () => (import('pages/invitation/Name.vue')),
    meta: {
      middleware: ['invitation'],
      pageLevel: 2,
    }
  },
  {
    path: 'invitations/:invitationId/phone',
    name: 'invitation-phone',
    component: () => (import('pages/invitation/Phone.vue')),
    meta: {
      middleware: ['invitation'],
      pageLevel: 3,
    }
  },
  {
    path: 'expired-invitation',
    name: 'expired-invitation',
    component: () => (import('pages/ExpiredInvitation.vue')),
  },

  /**************************************/
  {
    path: 'wrong-access',
    name: 'wrong-access',
    component: () => (import('pages/WrongAccess.vue')),
    meta: {
      middleware: ['authenticated'],
    },
  },
  {
    path: 'bizs/:bizId/other-biz-verified',
    name: 'other-biz-verified',
    component: () => (import('pages/OtherBizVerified.vue')),
    meta: {
      middleware: ['authenticated'],
    },
    props(ctx) {
      const {params} = ctx
      return {
        ...params,
      }
    }
  },
  /**************************************
  * subsidy
  */
  {
    path: 'bizs/:bizId/subsidy',
    name: 'subsidy',
    component: () => (import('@/pages/Subsidy.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
    },
    props(ctx) {
      return {
        translation: 'pages.subsidy',
        ...ctx.query,
      }
    },
  },
  /**************************************
  * taxrefund
  */
  {
    path: 'bizs/:bizId/taxrefund',
    name: 'taxrefund',
    component: () => (import('@/pages/Taxrefund.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
    },
    props(ctx) {
      return {
        translation: 'pages.taxrefund',
        ...ctx.query,
      }
    },
  },
  /**************************************
  * taxrefund
  */
    {
      path: 'bizs/:bizId/contract',
      name: 'contract',
      component: () => (import('@/pages/Contract.vue')),
      meta: {
        middleware: ['authenticated', 'set-biz'],
      },
      props(ctx) {
        return {
          ...ctx.query,
        }
      },
    },
  /**************************************
  * user-profile
  */
  {
    path: 'user/profile/:bizId',
    name: 'user-profile',
    component: () => (import('@/pages/user-info/UserProfile.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
    },
    props(ctx) {
      return {
        ...ctx.query,
      }
    },
  },
  /**************************************
  * user-contract
  */
  {
    path: 'user/contract/:bizId',
    name: 'user-contract',
    component: () => (import('@/pages/user-info/UserContract.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
    },
    props(ctx) {
      return {
        ...ctx.query,
      }
    },
  },
  /**************************************
  * user-contract
  */
  {
    path: 'user/contract-detail/:bizId',
    name: 'user-contract-detail',
    component: () => (import('@/pages/user-info/UserContractDetail.vue')),
    meta: {
      middleware: ['authenticated', 'set-biz'],
    },
    props(ctx) {
      return {
        ...ctx.query,
      }
    },
  },
  /**************************************
  * user-account
  */
  {
    path: 'user/account',
    name: 'user-account',
    component: () => (import('@/pages/user-info/UserAccount.vue')),
    meta: {
      middleware: ['authenticated'],
    },
    props(ctx) {
      return {
        ...ctx.query,
      }
    },
  },
  /**************************************
  * subsidy
  */
    {
      path: '/personalization',
      name: 'personalization',
      component: () => (import('@/pages/personalization/Index.vue')),
      props(ctx) {
        return {
          ...ctx.query,
        }
      },
    },
  /**************************************/
  {
    path: '*',
    redirect: (ctx) => {
      const {path = ''} = ctx
      const {
        redirect = () => ({name: 'home'})
      } = redirectPaths.find((item) => item.path.test(path)) || {}
      return redirect(ctx)
    },
  }
]

export default routes
